/**
 * The order a crew member works a day in — one rule for every crew screen.
 *
 * A route is worked in its route order (route_position, which the server
 * already backfills from the series when a visit has no route_jobs row). A
 * stop that is not on a route goes by its own time. Routes and loose stops
 * are then laid out by when each one starts: a route block by its earliest
 * stop's time. Sorting a route by time_start alone put any stop whose clock
 * was missing or stale at the bottom (Ryan saw Brianna last, Sep 29 2026),
 * and `Number(position) || 9999` sent the FIRST stop (position 0) last.
 *
 * Within a route, route_seq wins when the server sent it: the stop's place in
 * the Routes page order exactly, neighbour pairs as one block and ties as
 * the Routes page breaks them (server/services/routeOrder; owner, Sep 29
 * 2026: "MY route order is the right one").
 */
type Job = {
  id: number | string; scheduled_date?: string | null; time_start?: string | null;
  route_id?: number | string | null; route_position?: number | string | null; route_seq?: number | null;
};

const hasSeq = (j: Job) => j.route_seq !== null && j.route_seq !== undefined && Number.isFinite(Number(j.route_seq));

const hasPos = (j: Job) => j.route_position !== null && j.route_position !== undefined && j.route_position !== '' && Number.isFinite(Number(j.route_position));
const t = (j: Job) => (j.time_start ? String(j.time_start).slice(0, 5) : '99:99');
const day = (j: Job) => String(j.scheduled_date || '').slice(0, 10);

/** A new array in working order: by day, then route blocks and loose stops by start time, route stops in route order. */
export function inWorkingOrder<T extends Job>(jobs: T[]): T[] {
  // Each route block on a day starts when its earliest stop does.
  const blockStart = new Map<string, string>();
  for (const j of jobs) {
    if (j.route_id == null) continue;
    const k = `${day(j)}|${j.route_id}`;
    const cur = blockStart.get(k);
    if (!cur || t(j) < cur) blockStart.set(k, t(j));
  }
  const startOf = (j: Job) => (j.route_id == null ? t(j) : blockStart.get(`${day(j)}|${j.route_id}`) || t(j));
  return jobs.slice().sort((a, b) => {
    const d = day(a).localeCompare(day(b));
    if (d) return d;
    const s = startOf(a).localeCompare(startOf(b));
    if (s) return s;
    const ra = a.route_id == null ? '' : String(a.route_id), rb = b.route_id == null ? '' : String(b.route_id);
    if (ra !== rb) return ra.localeCompare(rb, undefined, { numeric: true });
    if (a.route_id != null) {
      // Same route: the Routes page order (route_seq), else route position;
      // a stop with no position at all after the ones that have one.
      if (hasSeq(a) && hasSeq(b)) return Number(a.route_seq) - Number(b.route_seq);
      const pa = hasPos(a) ? Number(a.route_position) : Infinity, pb = hasPos(b) ? Number(b.route_position) : Infinity;
      if (pa !== pb) return pa - pb;
    }
    return t(a).localeCompare(t(b)) || Number(a.id) - Number(b.id);
  });
}

/** A route stop's position for display/maps: 0 is a real position, missing is last. */
export function positionOf(j: Job): number {
  if (hasSeq(j)) return Number(j.route_seq);
  return hasPos(j) ? Number(j.route_position) : 9999;
}
